"use client";

import React from "react";
import { CreditCard, CircleQuestionMark, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqData = [{
  id: 1,
  question: "Como é realizada a implantação do ERP Microvix?",
  answer: "Nossa metodologia é 100% consultiva e guiada por especialistas Linx. Realizamos um diagnóstico profundo dos seus processos, configuração personalizada do sistema, treinamento prático das equipes e acompanhamento assistido durante o 'go-live' para garantir o sucesso da operação."
}, {
  id: 2,
  question: "Quais as principais vantagens competitivas do Microvix?",
  answer: "Sendo uma solução 100% em nuvem, o Microvix entrega mobilidade total para o gestor. Destaca-se pelo PDV de alta performance, gestão de estoque inteligente, controle rigoroso de metas e dashboards em tempo real que facilitam a tomada de decisão estratégica."
}, {
  id: 3,
  question: "O Microvix possui integração com canais de venda online?",
  answer: "Sim, oferecemos uma operação omnichannel completa. O Microvix integra-se nativamente com as maiores plataformas de e-commerce e marketplaces do mercado, centralizando estoque, pedidos e faturamento em um único ecossistema digital."
}, {
  id: 4,
  question: "Como funciona o suporte técnico pós-implantação?",
  answer: "Contamos com um time de suporte altamente especializado nas regras de negócio do varejo. O atendimento é ágil e focado em manter sua operação ativa 24/7, garantindo que você extraia o máximo potencial das funcionalidades do sistema."
}, {
  id: 5,
  question: "Qual o modelo de investimento para adquirir a solução?",
  answer: "O investimento é composto por uma taxa de setup (implantação e treinamento) e uma mensalidade recorrente escalável, personalizada conforme o porte da sua empresa, número de usuários e módulos contratados."
}];
const billingData = [{
  id: 1,
  service: "Diagnóstico de Ecossistema",
  duration: "Sessão Inicial",
  fee: "Sob Consulta",
  description: "Análise profunda dos processos para recomendação da melhor arquitetura de software."
}, {
  id: 2,
  service: "Setup & Implantação",
  duration: "Projeto",
  fee: "Personalizado",
  description: "Configuração técnica, migração de dados legados e treinamento operacional completo."
}, {
  id: 3,
  service: "Consultoria Estratégica",
  duration: "Mensal",
  fee: "Recorrente",
  description: "Acompanhamento dedicado para otimização de processos e suporte prioritário."
}, {
  id: 4,
  service: "Integração Marketplace",
  duration: "Por Canal",
  fee: "Setup Fixo",
  description: "Configuração de hubs de integração para expansão de canais de venda digitais."
}];
export default function Faq() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="150391" id="faq" className="bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      <style>{`
        :root {
          --fume-bg: hsla(0, 0%, 15%, 0.7);
        }
        #faq, 
        #faq .bg-card,
        #faq .bg-popover,
        #faq .bg-modal,
        #faq .bg-section {
          background-color: var(--fume-bg) !important;
          backdrop-filter: blur(12px) !important;
        }
        #faq *,
        #faq .bg-card * {
          color: var(--foreground);
        }
      `}</style>
      
      <div className="bg-primary/5 absolute top-0 left-1/2 -z-10 h-[800px] w-[800px] -translate-x-1/2 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <Badge variant="secondary" className="mb-4 rounded-full px-4 py-1 text-sm bg-accent text-accent-foreground border-primary/20">
            <Sparkles className="text-primary mr-2 size-4" />
            Central de Ajuda
          </Badge>
          <h2 className="font-sans text-foreground text-4xl font-bold tracking-tight sm:text-5xl uppercase">
            Dúvidas Frequentes
          </h2>
          <p className="text-muted-foreground mt-6 text-lg leading-8">
            Encontre respostas detalhadas sobre nossas soluções, processos de implantação e como as soluções Linx Microvix podem acelerar o crescimento da sua empresa.
          </p>
        </div>

        <div ref={contentRef} className={`mt-16 transition-all delay-200 duration-1000 ${isContentVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <Tabs defaultValue="faq" className="mx-auto max-w-5xl">
            <div className="mb-12 flex justify-center !rounded-none">
              <TabsList className="bg-[var(--fume-bg)] rounded-[32px] p-2 backdrop-blur-md border border-border/50">
                <TabsTrigger value="faq" className="rounded-[24px] px-6 py-3 text-base data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg transition-all">FAQ</TabsTrigger>
                <TabsTrigger value="billing" className="rounded-[24px] px-6 py-3 text-base data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg transition-all">
                  Investimento
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="faq" className="focus-visible:outline-none focus-visible:ring-0">
              <div className="bg-card border-border/50 mx-auto max-w-3xl rounded-[24px] border p-8 shadow-sm sm:p-12">
                <div className="mb-8 flex items-center gap-4">
                  <div className="bg-primary text-primary-foreground flex size-12 items-center justify-center rounded-full shadow-lg shadow-primary/20">
                    <CircleQuestionMark className="size-6" />
                  </div>
                  <h3 className="font-sans text-3xl font-bold text-foreground">Perguntas Comuns</h3>
                </div>
                <div className="space-y-8">
                  {faqData.map((faq, i) => <div key={faq.id} data-index={i} className="border-b border-border/50 pb-6 last:border-0 last:pb-0">
                      <h4 className="text-foreground text-lg font-bold mb-3">
                        {faq.question}
                      </h4>
                      <p className="text-muted-foreground text-base leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>)}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="billing" className="focus-visible:outline-none focus-visible:ring-0">
              <div className="bg-card border-border/50 mx-auto max-w-4xl overflow-hidden rounded-[24px] border shadow-sm">
                <div className="border-border/50 border-b p-8 sm:p-10 bg-muted/30">
                  <div className="flex items-center gap-4">
                    <div className="bg-primary text-primary-foreground flex size-12 items-center justify-center rounded-full shadow-lg shadow-primary/20">
                      <CreditCard className="size-6" />
                    </div>
                    <div>
                      <h3 className="font-sans text-3xl font-bold text-foreground">Investimento em Tecnologia</h3>
                      <p className="text-muted-foreground mt-2">Transparência e foco em resultados para sua operação varejista.</p>
                    </div>
                  </div>
                </div>
                <div className="p-0 sm:p-6 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/50 hover:bg-transparent">
                        <TableHead className="w-[250px] text-base font-bold text-foreground">Serviço</TableHead>
                        <TableHead className="text-base font-bold text-foreground">Descrição</TableHead>
                        <TableHead className="text-base font-bold text-foreground">Modelo</TableHead>
                        <TableHead className="text-right text-base font-bold text-foreground">Taxa</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {billingData.map((item, i) => <TableRow data-index={i} key={item.id} className="border-border/50 hover:bg-primary/5 transition-colors">
                          <TableCell className="font-bold text-foreground">{item.service}</TableCell>
                          <TableCell className="text-muted-foreground">{item.description}</TableCell>
                          <TableCell>{item.duration}</TableCell>
                          <TableCell className="text-right font-bold text-primary">{item.fee}</TableCell>
                        </TableRow>)}
                    </TableBody>
                  </Table>
                </div>
                <div className="bg-secondary/5 border-border/50 flex flex-col items-center justify-between gap-4 border-t p-8 sm:flex-row sm:p-10">
                  <p className="text-muted-foreground text-sm max-w-md">
                    Os valores variam de acordo com o porte da empresa e volume de transações. Solicite um orçamento detalhado para seu projeto.
                  </p>
                  <Button asChild className="rounded-full px-8 bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl shadow-primary/20 transition-all hover:scale-105">
                    <Link to="/ContactsPage">Solicitar Demonstração</Link>
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>;
}